import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  STORE_TYPES, fetchStores, createStore, fetchStoreStatus, deleteStore,
} from "../../services/storesService";


const STORE_TYPE_META = {
  TikTok: { icon: "🎵", color: "#ff0050" },
  Snap: { icon: "👻", color: "#f5a623" },
  Meta: { icon: "📘", color: "#1877f2" },
  Shopify: { icon: "🛍️", color: "#96bf48" },
  LinkedIn: { icon: "💼", color: "#0a66c2" },
  Other: { icon: "🌐", color: "#64748b" },
};

const STATUS_STYLE = {
  Pending: "bg-gray-100 text-gray-600",
  "In Progress": "bg-blue-100 text-blue-700",
  Complete: "bg-green-100 text-green-700",
  Failed: "bg-red-100 text-red-700",
};

const isTerminal = (s) => s === "Complete" || s === "Failed";

// ── Modale « Add Store » ──────────────────────────────────────
const AddStoreModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [storeType, setStoreType] = useState("Shopify");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.warn("Le nom de la boutique est obligatoire.");
    setSubmitting(true);
    try {
      const store = await createStore({ name: name.trim(), storeType, websiteUrl: websiteUrl.trim() });
      toast.success("Boutique créée — provisioning en cours…");
      onCreated(store);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Création impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-bold text-[#221C51] text-base">➕ Ajouter une boutique</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#221C51] mb-1">Nom de la boutique *</label>
            <input
              value={name} onChange={(e) => setName(e.target.value)} autoFocus
              placeholder="Ma boutique"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]"
            />
            <p className="text-xs text-gray-400 mt-1">Sert de nom au conteneur GTM et à la propriété GA4.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#221C51] mb-1">Type de boutique</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {STORE_TYPES.map((t) => (
                <button
                  key={t} type="button" onClick={() => setStoreType(t)}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs font-semibold transition ${
                    storeType === t ? "border-[#221C51] bg-[#221C51]/5 text-[#221C51]" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <span className="text-lg">{STORE_TYPE_META[t].icon}</span>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#221C51] mb-1">URL du site</label>
            <input
              value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://ma-boutique.com"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]"
            />
            <p className="text-xs text-gray-400 mt-1">Utilisée comme URL par défaut du data stream GA4.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100">Annuler</button>
          <button onClick={submit} disabled={submitting} className="px-5 py-2 text-sm text-white bg-[#221C51] rounded-lg hover:bg-[#2e265e] disabled:opacity-50 font-semibold">
            {submitting ? "Création…" : "Provisionner →"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Carte boutique ────────────────────────────────────────────
const StoreCard = ({ store, onDelete }) => {
  const meta = STORE_TYPE_META[store.store_type] || STORE_TYPE_META.Other;
  const [showSnippet, setShowSnippet] = useState(false);
  const snippet = store.provisioning_result?.gtmSnippet;

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copié !"); };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: `${meta.color}1a` }}>{meta.icon}</div>
          <div className="min-w-0">
            <p className="font-bold text-[#221C51] truncate">{store.name}</p>
            <p className="text-xs text-gray-400">{store.store_type}{store.website_url ? ` · ${store.website_url}` : ""}</p>
          </div>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${STATUS_STYLE[store.provisioning_status] || "bg-gray-100 text-gray-600"}`}>
          {store.provisioning_status === "In Progress" && <span className="inline-block w-2 h-2 mr-1 rounded-full bg-blue-500 animate-pulse align-middle" />}
          {store.provisioning_status}
        </span>
      </div>

      {store.provisioning_status === "Complete" && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-[10px] uppercase font-bold text-gray-400">Conteneur GTM</p>
            <button className="text-sm font-mono font-semibold text-[#221C51] hover:underline" onClick={() => copy(store.gtm_container_id)}>{store.gtm_container_id}</button>
          </div>
          <div className="bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-[10px] uppercase font-bold text-gray-400">Measurement ID</p>
            <button className="text-sm font-mono font-semibold text-[#221C51] hover:underline" onClick={() => copy(store.measurement_id)}>{store.measurement_id}</button>
          </div>
        </div>
      )}

      {store.provisioning_status === "Failed" && (
        <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{store.error_message || "Échec du provisioning."}</p>
      )}

      <div className="mt-4 flex items-center gap-3">
        {snippet && (
          <button onClick={() => setShowSnippet((s) => !s)} className="text-xs font-semibold text-[#221C51] hover:underline">
            {showSnippet ? "Masquer le snippet" : "Voir le snippet GTM"}
          </button>
        )}
        <button onClick={() => onDelete(store)} className="text-xs text-gray-400 hover:text-red-500 ml-auto">Supprimer</button>
      </div>

      {showSnippet && snippet && (
        <div className="mt-3 relative">
          <pre className="text-[11px] bg-[#0F0B2D] text-green-300 rounded-lg p-3 overflow-x-auto max-h-48 whitespace-pre-wrap">{snippet}</pre>
          <button onClick={() => copy(snippet)} className="absolute top-2 right-2 text-[10px] bg-white/10 text-white px-2 py-1 rounded hover:bg-white/20">Copier</button>
        </div>
      )}
    </div>
  );
};

// ── Écran principal ──────────────────────────────────────────
const StoreManager = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { setStores(await fetchStores() || []); } catch { /* toast géré ailleurs */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = useMemo(() => stores.filter((s) => !isTerminal(s.provisioning_status)), [stores]);

  // Polling tant qu'une boutique est en cours de provisioning.
  useEffect(() => {
    if (pending.length === 0) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const updates = await Promise.all(pending.map((s) => fetchStoreStatus(s.id).catch(() => null)));
        setStores((prev) => prev.map((s) => {
          const u = updates.find((x) => x && x.id === s.id);
          if (!u) return s;
          return { ...s, provisioning_status: u.status, active: u.active, gtm_container_id: u.gtmContainerId, measurement_id: u.measurementId, ga4_property_id: u.ga4PropertyId, error_message: u.errorMessage, provisioning_result: u.result || s.provisioning_result };
        }));
      } catch { /* silencieux */ }
    }, 3000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [pending]);

  const onCreated = (store) => setStores((prev) => [store, ...prev]);

  const onDelete = async (store) => {
    if (!window.confirm(`Supprimer « ${store.name} » ?`)) return;
    try { await deleteStore(store.id); setStores((prev) => prev.filter((s) => s.id !== store.id)); toast.success("Boutique supprimée."); }
    catch (e) { toast.error(e?.response?.data?.message || "Suppression impossible."); }
  };

  return (
    <div className="p-2" data-tour="stores-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#221C51]">Stores</h2>
          <p className="text-sm text-gray-400 mt-0.5">Provisionnez GTM + GA4 et le pixel pour chaque boutique.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2e265e] transition shadow-md">
          <span className="text-lg leading-none">＋</span> Add Store
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <div className="w-8 h-8 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin mb-4" />
          <p className="text-sm">Chargement des boutiques…</p>
        </div>
      ) : stores.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="text-5xl mb-3">🏬</div>
          <p className="font-semibold text-[#221C51]">Aucune boutique</p>
          <p className="text-sm text-gray-400 mt-1 mb-5">Ajoutez votre première boutique pour lancer le provisioning GTM/GA4.</p>
          <button onClick={() => setShowModal(true)} className="px-5 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2e265e]">＋ Add Store</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stores.map((s) => <StoreCard key={s.id} store={s} onDelete={onDelete} />)}
        </div>
      )}

      {showModal && <AddStoreModal onClose={() => setShowModal(false)} onCreated={onCreated} />}
    </div>
  );
};

export default StoreManager;
